import { useBreakpoints } from "constants/constants";

import {
  useFindDataDumpForTrendChart,
  useFocusRange,
  useFullRangeDataDump,
} from "utils/customHooks/genericHooks";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useTrendChartBarRanges } from "utils/customHooks/trendChartHooks";
import AheadBehindSummaryChart from "./AheadBehindSummaryChart";
import {
  useFixedTargetsOverride,
  useOutputsAndTargets,
} from "../aheadBehindUtils";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import { AheadBehindSummaryChartsStyles } from "./AheadBehindSummaryChartsStyles";
import { OperatorPageMetrics } from "../OperatorPageMetrics";
import styled from "styled-components/macro";
import { useVisibleMetrics } from "components/Dashboard/GroupMetrics/useVisibleMetrics";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";

/** Includes AheadBehindSummaryChart components for "Hour" and "Shift,"
 * and displays current output counts respectively for comparision
 */
export default function AheadBehindSummaryCharts() {
  const currentHourStart = useCurrentHourStartMs();

  const dataDumpFullRange = useFullRangeDataDump();
  /** An hourly data dump, which comes from the trend chart data dumps when binSize = 1 hour.
   * The binSize is set where data dumps are retrieved on the Operator page
   */
  const dataDumpCurrentHour = useFindDataDumpForTrendChart({
    geToFind: currentHourStart ?? null,
  });

  const {
    currentFixedTarget: currentFixedHourTarget,
    fixedTarget: totalFixedHourTarget,
    currentFixedShiftTarget,
    totalFixedShiftTarget,
  } = useFixedTargetsOverride(dataDumpCurrentHour, dataDumpFullRange);

  const processId = useProcessIdOnSingleProcessPage();
  const {
    productionPotentialOutput: hourCurrentTargetOutput,
    netOutput: hourOutput,
    outputLossSum: hourOutputLossSum,
    totalPotentialOutput: hourTotalTargetOutput,
  } = useOutputsAndTargets(
    dataDumpCurrentHour,
    processId,
    currentFixedHourTarget,
    totalFixedHourTarget
  );

  const {
    productionPotentialOutput: shiftCurrentTargetOutput,
    netOutput: shiftOutput,
    outputLossSum: shiftOutputLossSum,
    totalPotentialOutput: shiftTotalTargetOutput,
  } = useOutputsAndTargets(
    dataDumpFullRange,
    processId,
    currentFixedShiftTarget,
    totalFixedShiftTarget
  );

  const [flightConfig] = useFlightConfig();
  const isColorOnlyConfigAvailable =
    (processId &&
      flightConfig.general.aheadBehindScaleTargetRateColorOnly[processId]) ||
    flightConfig.general.aheadBehindScaleTargetRateColorOnly["ALL"];

  const { outputLossSum: hourOutputLossSumColorOnly } = useOutputsAndTargets(
    dataDumpCurrentHour,
    processId,
    currentFixedHourTarget,
    totalFixedHourTarget,
    true
  );

  const { outputLossSum: shiftOutputLossSumColorOnly } = useOutputsAndTargets(
    dataDumpFullRange,
    processId,
    currentFixedShiftTarget,
    totalFixedShiftTarget,
    true
  );

  const { isTabletOrLarger } = useBreakpoints();

  const hourCurrentTarget = currentFixedHourTarget ?? hourCurrentTargetOutput;
  const hourCount = hourOutput;
  const hourTotalTarget = totalFixedHourTarget ?? hourTotalTargetOutput;

  const shiftCurrentTarget =
    currentFixedShiftTarget ?? shiftCurrentTargetOutput;
  const shiftCount = shiftOutput;
  const shiftTotalTarget = totalFixedShiftTarget ?? shiftTotalTargetOutput;

  const operatorMetricsProps = {
    dataDumpCurrentHour,
    hourCurrentTarget,
    hourCount,
    hourTotalTarget,
    dataDumpCurrentShift: dataDumpFullRange,
    shiftCurrentTarget,
    shiftCount,
    shiftTotalTarget,
    horizontalLayout: isTabletOrLarger,
  };

  const visibleMetrics = useVisibleMetrics();

  // find the first metric with "onlyShowOnPages" including "operatorPreferred"
  const firstOperatorPreferredMetric = visibleMetrics.GroupMetrics?.find(
    metric =>
      metric.onlyShowOnPages?.includes("operatorPreferred") &&
      // ignore if it's hidden
      visibleMetrics.GroupMetrics.find(
        m => m.dataDumpKey === metric.dataDumpKey
      )
  );

  const metricDisplayOverrideHour = getMetricOverride(
    firstOperatorPreferredMetric,
    dataDumpCurrentHour
  );

  const metricDisplayOverrideShift = getMetricOverride(
    firstOperatorPreferredMetric,
    dataDumpFullRange
  );

  return (
    <AheadBehindSummaryChartsStyles
      $isCompactLayout={Boolean(!isTabletOrLarger)}
    >
      <div className="progressSummaries">
        <AheadBehindSummaryChart
          timeIntervalName="Hour"
          currentOutput={hourOutput}
          currentTargetOutput={
            currentFixedHourTarget ?? hourCurrentTargetOutput
          }
          totalTargetOutput={totalFixedHourTarget ?? hourTotalTargetOutput}
          outputLossSum={hourOutputLossSum}
          outputLossSumColorOnly={
            isColorOnlyConfigAvailable ? hourOutputLossSumColorOnly : null
          }
          metricDisplayOverride={metricDisplayOverrideHour}
        />
        {!isTabletOrLarger && (
          <OperatorPageMetrics {...operatorMetricsProps} hourOrShift={"hour"} />
        )}
        <div className="space" />
        <AheadBehindSummaryChart
          currentOutput={shiftOutput}
          currentTargetOutput={
            currentFixedShiftTarget ?? shiftCurrentTargetOutput
          }
          totalTargetOutput={totalFixedShiftTarget ?? shiftTotalTargetOutput}
          outputLossSum={shiftOutputLossSum}
          outputLossSumColorOnly={
            isColorOnlyConfigAvailable ? shiftOutputLossSumColorOnly : null
          }
          timeIntervalName="Shift"
          metricDisplayOverride={metricDisplayOverrideShift}
        />
        {!isTabletOrLarger && (
          <OperatorPageMetrics
            {...operatorMetricsProps}
            hourOrShift={"shift"}
          />
        )}
      </div>
      {isTabletOrLarger && (
        <OperatorPageMetrics {...operatorMetricsProps} hourOrShift={"both"} />
      )}
    </AheadBehindSummaryChartsStyles>
  );
}

function getMetricOverride(metric, dataDump) {
  if (!metric) {
    return null;
  }

  // if it's a metric whose dataDumpKey ends with "percent", turn it into a 3-row element like "<displayName>" <br> "<value>" <br> "%"
  let metricOverride: string | JSX.Element = dataDump?.[metric.dataDumpKey]
    ? Number(dataDump[metric.dataDumpKey]).toFixed(0)
    : "--";
  if (metric.dataDumpKey.toLowerCase().endsWith("percent")) {
    metricOverride = (
      <MetricDisplayElement
        name={metric.displayName}
        value={metricOverride}
        units="%"
      />
    );
  }
  return metricOverride;
}

function useCurrentHourStartMs() {
  const focusRange = useFocusRange();
  const isStorybook = useIsStorybook();
  const trendChartBarsRanges = useTrendChartBarRanges();
  if (!focusRange) {
    return null;
  }

  const now = new Date().getTime();

  return isStorybook
    ? trendChartBarsRanges[0].ge
    : trendChartBarsRanges.find(range => range.ge <= now && now < range.lt)?.ge;
}

const MetricDisplayElement = ({ name, value, units }) => (
  <MetricDisplayElementStyles>
    <div className="name">{name}</div>
    <div className="value">{value}</div>
    <div className="units">{units}</div>
  </MetricDisplayElementStyles>
);
const MetricDisplayElementStyles = styled.div`
  .name,
  .units,
  .value {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .name,
  .units {
    font-size: 0.5em;
    height: 1em;
  }
`;
